import { SELECTED_ACCOUNT_CHAIN_ID } from '@hoodmorning/contracts';
import { concatHex, decodeFunctionData, parseAbi, toFunctionSelector, type PublicClient } from 'viem';
import { prepareExternalExecutorSetup, prepareSelectedExternalExecutorSetup, type ExternalExecutorSetupInput, type ExternalExecutorSetupPlan } from './externalExecutorSetup.js';

const accountAbi = parseAbi([
  'function getExecutionData(bytes4 selector) view returns((address module,bool skipRuntimeValidation,bool allowGlobalValidation,bytes25[] executionHooks))',
  'function getValidationData(bytes24 entity) view returns((uint8 validationFlags,bytes25[] validationHooks,bytes25[] executionHooks,bytes4[] selectors))',
  'function getFallbackSignerData() view returns(address,bool)',
]);
const setupAbi = parseAbi([
  'function installExecution(address module,((bytes4 executionSelector,bool skipRuntimeValidation,bool allowGlobalValidation)[] executionFunctions,(bytes4 executionSelector,uint32 entityId,bool isPreHook,bool isPostHook)[] executionHooks,bytes4[] interfaceIds) manifest,bytes data)',
]);
const engineAbi = parseAbi([
  'function globalEpoch() view returns(uint256)', 'function epoch() view returns(uint256)',
  'function armed() view returns(bool)', 'function healthy() view returns(bool)',
  'function nextOrder() view returns(uint256)', 'function moduleId() pure returns(string)',
]);

function executorTopologyReader<const ChainId extends 4663 | 46630>(
  prepareExternalExecutorSetup: (input: ExternalExecutorSetupInput<ChainId>) => ExternalExecutorSetupPlan<ChainId>) {
  /**
   * Facts for the slot-2 external runtime at a caller-bound block. Other modules'
   * hooks are retained for a separately reviewed coexistence/history decision.
   * SMA-B validation getters omit the stored module identity: matching fields
   * cannot prove the selected engine occupies slot 2 or has never been installed.
   * No installation, re-arm, route approval or authorization follows from this read.
   */
  async function readExternalExecutorTopologyAtBlock(input: {
    client: Pick<PublicClient, 'readContract'>;
    setup: ExternalExecutorSetupInput<ChainId>;
    blockNumber: bigint;
  }) {
    const setup = prepareExternalExecutorSetup(structuredClone(input.setup));
    const blockNumber = input.blockNumber;
    const readContract = input.client.readContract.bind(input.client) as PublicClient['readContract'];
    if (typeof blockNumber !== 'bigint' || blockNumber < 0n) throw new Error('Invalid external topology source');
    const manifest = decodeFunctionData({ abi: setupAbi, data: setup.steps[0].call.data });
    if (manifest.functionName !== 'installExecution') throw new Error('Invalid external setup manifest');
    const requiredHook = concatHex([setup.engine, '0x00000001', '0x04']);
    const validationEntity = concatHex([setup.engine, '0x00000002']);
    const execution = [];
    for (const hook of manifest.args[1].executionHooks) {
      const data = await readContract({ address: setup.vault, abi: accountAbi,
        functionName: 'getExecutionData', args: [hook.executionSelector], blockNumber });
      const selected = data.executionHooks.filter(value => value.slice(0, 42).toLowerCase() === setup.engine);
      execution.push({ selector: hook.executionSelector, ...data,
        otherModuleHooks: data.executionHooks.filter(value => value.slice(0, 42).toLowerCase() !== setup.engine),
        selectedHookShape: selected.length === 0 ? 'absent' as const
          : selected.length === 1 && selected[0]!.toLowerCase() === requiredHook ? 'expected' as const : 'conflicting' as const,
        nativeSelectorExpected: data.module.toLowerCase() === setup.vault
          && !data.skipRuntimeValidation && data.allowGlobalValidation,
      });
    }
    const validation = await readContract({ address: setup.vault, abi: accountAbi,
      functionName: 'getValidationData', args: [validationEntity], blockNumber });
    const noHooks = validation.validationHooks.length === 0 && validation.executionHooks.length === 0;
    const validationShape = validation.validationFlags !== 0 || !noHooks ? 'conflicting' as const
      : validation.selectors.length === 0 ? 'empty' as const
        : validation.selectors.length === 1
          && validation.selectors[0]!.toLowerCase() === toFunctionSelector('executeBatch((address,uint256,bytes)[])')
          ? 'expected' as const : 'conflicting' as const;
    const [globalEpoch, epoch, armed, healthy, nextOrder, moduleId, [owner, ownerDisabled]] = await Promise.all([
      readContract({ address: setup.engine, abi: engineAbi, functionName: 'globalEpoch', blockNumber }),
      readContract({ address: setup.engine, abi: engineAbi, functionName: 'epoch', blockNumber }),
      readContract({ address: setup.engine, abi: engineAbi, functionName: 'armed', blockNumber }),
      readContract({ address: setup.engine, abi: engineAbi, functionName: 'healthy', blockNumber }),
      readContract({ address: setup.engine, abi: engineAbi, functionName: 'nextOrder', blockNumber }),
      readContract({ address: setup.engine, abi: engineAbi, functionName: 'moduleId', blockNumber }),
      readContract({ address: setup.vault, abi: accountAbi, functionName: 'getFallbackSignerData', blockNumber }),
    ]);
    if (moduleId !== setup.moduleVersion || globalEpoch !== epoch) throw new Error('External topology module mismatch');
    return { kind: 'external-executor-topology' as const, version: setup.version, moduleVersion: setup.moduleVersion,
      vault: setup.vault, engine: setup.engine, sourceBlockNumber: blockNumber,
      execution, validation, validationEntity, validationShape, globalEpoch, armed, healthy, nextOrder, owner, ownerDisabled,
      validationStorageOwnerVerified: false as const, absenceProven: false as const,
      setupAuthorized: false as const, modulesVerified: false as const, canonicalityVerified: false as const,
      coexistenceVerified: false as const, routeConfigured: false as const,
    };
  }
  return readExternalExecutorTopologyAtBlock;
}

export const readExternalExecutorTopologyAtBlock: ReturnType<typeof executorTopologyReader<4663>> = executorTopologyReader<4663>(prepareExternalExecutorSetup);
export const readSelectedExternalExecutorTopologyAtBlock: ReturnType<typeof executorTopologyReader<typeof SELECTED_ACCOUNT_CHAIN_ID>> = executorTopologyReader<typeof SELECTED_ACCOUNT_CHAIN_ID>(prepareSelectedExternalExecutorSetup);
export type ExternalExecutorTopology<ChainId extends 4663 | 46630 = 4663> = Awaited<ReturnType<ReturnType<typeof executorTopologyReader<ChainId>>>>;
export type SelectedExternalExecutorTopology = ExternalExecutorTopology<typeof SELECTED_ACCOUNT_CHAIN_ID>;
